<script setup lang="ts">
import { debounce } from "lodash-es";
import { ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { maskDateBR } from "@/common/utils/mask";

import { getDate } from "@/common/utils/dates";

import { baseInput, baseDate } from "@/common/components";

import { EquipmentType } from "@/common/types/equipment";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: Partial<EquipmentType> | null;
    title?: string;
    width?: string;
  }>(),
  {
    width: "100%",
  }
);

const payload = ref<Partial<EquipmentType>>({});

function update(key: keyof typeof payload.value) {
  emit("update-data", { [key]: payload.value[key] });
}

const debounceForm = debounce(update, 1000);

watchEffect(() => {
  if (props.data) {
    payload.value = { ...props.data };
  }
});
</script>

<template>
  <section class="form-details" :style="{ width: width }">
    <form v-if="payload">
      <base-input
        v-model="payload.brand"
        :value="payload.brand"
        @input="debounceForm('brand')"
      >
        <template #label>
          {{ t("common.labels.brand") }}
        </template>
      </base-input>

      <div class="flex flex-row gap-4">
        <base-input
          v-model="payload.model"
          :value="payload.model"
          @input="debounceForm('model')"
        >
          <template #label>
            {{ t("common.labels.model") }}
          </template>
        </base-input>

        <base-input
          v-model="payload.serial_number"
          :value="payload.serial_number"
          @input="debounceForm('serial_number')"
        >
          <template #label>
            {{ t("common.labels.serial_number") }}
          </template>
        </base-input>
      </div>

      <div class="flex flex-row gap-4">
        <base-date
          v-model="payload.fabrication_date"
          :value="payload.fabrication_date"
          :required="true"
          value-mask="YYYY-MM-DD"
          placeholder="--/--/----"
          @update:model-value="debounceForm('fabrication_date')"
        >
          <template #label> {{ t("common.labels.fabrication") }}</template>
        </base-date>

        <base-date
          v-model="payload.expired_date"
          :value="payload.expired_date"
          :required="true"
          value-mask="YYYY-MM-DD"
          placeholder="--/--/----"
          @update:model-value="debounceForm('expired_date')"
        >
          <template #label>
            {{ t("common.labels.expiration_date") }}
          </template>
        </base-date>
      </div>

      <div class="flex flex-row gap-4">
        <base-date
          v-model="payload.calibration_date"
          :value="payload.calibration_date"
          :required="true"
          value-mask="YYYY-MM-DD"
          placeholder="--/--/----"
          @update:model-value="debounceForm('calibration_date')"
        >
          <template #label> {{ t("common.labels.calibration_date") }}</template>
        </base-date>

        <base-date
          v-model="payload.next_calibration"
          :value="payload.next_calibration"
          :required="true"
          value-mask="YYYY-MM-DD"
          placeholder="--/--/----"
          @update:model-value="debounceForm('next_calibration')"
        >
          <template #label> {{ t("common.labels.next_calibration") }}</template>
        </base-date>
      </div>
    </form>
  </section>
</template>

<style lang="scss" scoped>
.form-details {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-block-end: 1rem;

  .subtitle {
    display: flex;
    flex-direction: column;
    padding-inline: 0.5rem;
    border-bottom: 1px solid var(--border);
    margin-block: 1rem;
    padding-block-end: 0.5rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
